import { useFormik } from 'formik';
import { UserRoundCheck, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { bgMinileague } from '@/assets/tournaments';
import { Button, Form, InputField, Modal } from '@/components';
import { Layout, Sidebar } from '@/components/GenericsTournaments';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMinileagueData } from '@/hooks/useMinileagueData';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  reactivateMiniLeague,
  requestCheckNameAvailability,
} from '@/services/minileagues';
import {
  createMLSchema,
  oneSpaceBetweenWordMinTwoChar,
  TEXTS,
  validateFieldError,
} from '@/utils';

type TypeProps = {
  showModal: boolean;
};

const initialState = {
  showModal: false,
};

const minileagueTips = [
  {
    icon: <Users className="text-gdt-status-green" size={18} />,
    text: 'Invitá a quien quieras.',
  },
  {
    icon: <UserRoundCheck className="text-gdt-status-green" size={18} />,
    text: 'Aceptá a quien se postule en tu Miniliga.',
  },
];

export const ReactivateMinileague = () => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();
  const navigate = useNavigate();
  const [state, setState] = useState<TypeProps>(initialState);
  const location = useLocation();
  const { MLname, MLid } = location.state as { MLname: string; MLid: number };
  const { updateMinileagueProperties } = useMinileagueData();

  useEffect(() => {
    if (MLname) {
      checkNameAvailability(MLname);
    }
  }, []);

  const {
    touched,
    errors,
    isSubmitting,
    isValid,
    setFieldValue,
    handleSubmit,
    values,
    handleBlur,
    setFieldError,
    setStatus,
    setFieldTouched,
    status,
  } = useFormik({
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: createMLSchema,
    initialValues: {
      groupName: MLname,
    },
    onSubmit: async (
      {
        groupName,
      }: {
        groupName: string;
      },
      { setSubmitting }
    ) => {
      if (groupName) {
        try {
          const response = await reactivateMiniLeague(MLid, groupName);

          if (response.estado) {
            setState({
              showModal: true,
            });
            updateMinileagueProperties({
              miniLiga: {
                id: response.miniLiga.id,
                name: groupName,
                length: 0,
              },
              estado: response.estado,
              mensaje: '',
            });
          } else {
            toast.show(
              response.nombreTomado === '1'
                ? TEXTS.nameExist
                : TEXTS.defaultError
            );
          }

          setSubmitting(false);
        } catch (error) {
          console.error(error);
          throw new Error(TEXTS.defaultError);
        }
      }
    },
  });

  const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;

  const handleNameTournamentValidation = async (groupName: string) => {
    if (groupName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
      // Check if the name is equal to the previous name
      checkNameAvailability(groupName);
    } else {
      setStatus(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

    setFieldValue('groupName', value);
    setFieldTouched('groupName', true, false);

    if (value.trim().length === 0) {
      setFieldError('groupName', TEXTS.minLetters);
      setStatus(false);
    } else {
      handleNameTournamentValidation(value);
    }
  };

  const checkNameAvailability = async (groupName: string) => {
    const response = await requestCheckNameAvailability(
      encodeURIComponent(groupName)
    );
    if (!response.estado && 'codError' in response) {
      if (response.codError === 1603) {
        setFieldError('groupName', TEXTS.nameExist);
        setStatus(false);
      } else {
        setFieldError('groupName', response.mensaje || TEXTS.defaultError);
        setStatus(false);
      }
    } else {
      setFieldError('groupName', undefined);
      setStatus(true);
    }
  };

  return (
    <Layout
      actionFooter={{
        actionHandler: handleSubmit,
        buttonName: 'Reactivar Miniliga',
        buttonDisabled:
          isSubmitting || !isValid || values.groupName?.length === 0,
      }}
      className="px-4 lg:w-[974px] lg:mt-6"
      main={() => (
        <div className="lg:w-[400px]">
          {isDesktop && (
            <>
              <Link
                className="text-gdt-blue font-bold relative left-[-15px]"
                to={ROUTES.minileagues.home}
              >
                <FiChevronLeft className="text-gdt-blue w-6 h-6 inline" />
                <span className="pl-[4px]">Volver</span>
              </Link>
              <div className="mt-[40px]">
                <span className="font-bold text-base text-gdt-gray lg:text-lg">
                  Reactivar Miniliga
                </span>
              </div>
            </>
          )}
          <p className="font-medium lg:font-semibold text-sm mt-4 text-gdt-gray-2">
            Podés cambiarle el nombre a tu Miniliga {MLname}
          </p>
          <div className="lg:w-[80%] mt-5 lg:mt-8">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.groupName,
                  errors.groupName,
                  values.groupName.length
                )}
                label="Nombre de Miniliga"
                maxLength={25}
                name="groupName"
                placeholder="Ingresar nombre"
                success={
                  !errors.groupName && values.groupName?.length > 0 && status
                }
                value={values.groupName}
                onBlur={(e: React.FocusEvent<any, Element>) => {
                  if (values.groupName.length < 2) {
                    handleBlur(e);
                  }
                }}
                onChange={handleInputChange}
              />
            </Form.Field>
          </div>
          {isDesktop && (
            <div className="w-52 mt-10">
              <Button
                className="w-full"
                disabled={
                  isSubmitting || !isValid || values.groupName?.length === 0
                }
                type="button"
                variant="primary"
                onClick={handleSubmit}
              >
                Reactivar Miniliga
              </Button>
            </div>
          )}

          <Modal
            bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
            className="px-4 lg:px-11 py-10 min-w-[550px]"
            closeOutside={false}
            controlledOnClose={() => null}
            isControlledOpen={state.showModal}
            render={() => {
              return (
                <div>
                  <p className="text-base font-semibold mb-[30px] text-center">
                    ¡Felicitaciones! La Miniliga fue reactivada con éxito. Se
                    envió un mail a todos tus amigos que participaron
                    invitándolos nuevamente
                    <br />
                    ¿Querés invitar más amigos ahora?
                  </p>
                  <div className="flex space-x-[10px] h-[50px]">
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.minileagues.home);
                      }}
                    >
                      Invitar más tarde
                    </Button>
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="primary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.minileagues.invite);
                      }}
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            showCloseButton={false}
            size="xs"
            variant="controlled"
          />
        </div>
      )}
      mobile={{
        leftContent: {
          backAction: () => {
            navigate(ROUTES.minileagues.home);
          },
        },
        centerContent: { title: 'Reactivar Miniliga' },
      }}
      sidebar={() =>
        isDesktop && (
          <Sidebar className="lg:w-[436px]">
            <div
              className="relative overflow-hidden rounded-md bg-no-repeat bg-left h-[290px] bg-cover"
              style={{
                backgroundImage: `url(${bgMinileague})`,
              }}
            >
              <div className="px-14 relative">
                <p className="text-[26px] font-normal font-bebas text-white mt-[70px]">
                  COMO ADMINISTRADOR DE UNA MINILIGA:
                </p>
                <ul className="space-y-7 mt-9">
                  {minileagueTips.map((tip, index) => (
                    <li key={index} className="flex items-center">
                      <figure className="w-7 h-7 bg-gdt-status-green-light rounded-full flex items-center justify-center">
                        {tip.icon}
                      </figure>
                      <span className="text-white ml-[5px]">{tip.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Sidebar>
        )
      }
      withBanners={false}
    />
  );
};
